/**
 * Google Analytics 4 (GA4) Integration
 * Track user behavior, page views, events, and conversions
 */

import ReactGA from 'react-ga4';

// Configuration
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
const isDevelopment = import.meta.env.DEV;

interface GAConfig {
  testMode?: boolean;
  debug?: boolean;
}

/**
 * Initialize Google Analytics
 * Call this once when the app starts
 */
export function initGA(config: GAConfig = {}): void {
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    console.warn('Google Analytics: No measurement ID provided');
    return;
  }

  try {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      testMode: config.testMode || isDevelopment,
      gaOptions: {
        debug_mode: config.debug || isDevelopment,
      },
    });
    
    if (isDevelopment) {
      console.log('Google Analytics initialized (test mode)');
    }
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
}

/**
 * Track page view
 * Call this on route changes
 */
export function trackPageView(path: string, title?: string): void {
  try {
    ReactGA.send({ 
      hitType: 'pageview', 
      page: path,
      title: title || document.title
    });
    
    if (isDevelopment) {
      console.log('GA Page View:', path, title);
    }
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
}

/**
 * Track custom event
 */
export function trackEvent(
  category: string,
  action: string,
  label?: string,
  value?: number
): void {
  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    
    if (isDevelopment) {
      console.log('GA Event:', { category, action, label, value });
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
}

/**
 * Track button click
 */
export function trackButtonClick(buttonName: string, location?: string): void {
  trackEvent('Button', 'Click', buttonName + (location ? ' - ' + location : ''));
}

/**
 * Track service view
 */
export function trackServiceView(serviceId: string, serviceName: string): void {
  trackEvent('Service', 'View', serviceName, undefined);
  
  // Send as GA4 event
  ReactGA.event('view_item', {
    item_id: serviceId,
    item_name: serviceName,
    item_category: 'Service',
  });
}

/**
 * Track add to cart
 */
export function trackAddToCart(item: {
  id: string;
  name: string;
  price: number;
  category?: string;
}): void {
  trackEvent('Cart', 'Add', item.name, item.price);
  
  ReactGA.event('add_to_cart', {
    currency: 'INR',
    value: item.price,
    items: [{
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Service',
      price: item.price,
      quantity: 1
    }]
  });
}

/**
 * Track remove from cart
 */
export function trackRemoveFromCart(item: {
  id: string;
  name: string;
  price: number;
}): void {
  trackEvent('Cart', 'Remove', item.name, item.price);
  
  ReactGA.event('remove_from_cart', {
    currency: 'INR',
    value: item.price,
    items: [{
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: 1
    }]
  });
}

/**
 * Track begin checkout
 */
export function trackBeginCheckout(cartValue: number, itemCount: number): void {
  trackEvent('Checkout', 'Begin', undefined, cartValue);
  
  ReactGA.event('begin_checkout', {
    currency: 'INR',
    value: cartValue,
    item_count: itemCount
  });
}

/**
 * Track checkout progress
 */
export function trackCheckoutStep(step: number, stepName: string): void {
  trackEvent('Checkout', 'Step', stepName, step);
  
  ReactGA.event('checkout_progress', {
    checkout_step: step,
    checkout_option: stepName
  });
}

/**
 * Track purchase/order completion
 */
export function trackPurchase(data: {
  orderId: string;
  value: number;
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    category?: string;
  }>;
  paymentMethod?: string;
  promoCode?: string;
  discount?: number;
}): void {
  trackEvent('Purchase', 'Complete', data.orderId, data.value);
  
  ReactGA.event('purchase', {
    transaction_id: data.orderId,
    value: data.value,
    currency: 'INR',
    payment_type: data.paymentMethod,
    coupon: data.promoCode,
    discount: data.discount,
    items: data.items.map(item => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Service',
      price: item.price,
      quantity: item.quantity
    }))
  });
}

/**
 * Track search
 */
export function trackSearch(query: string, resultsCount?: number): void {
  trackEvent('Search', 'Query', query, resultsCount);
  
  ReactGA.event('search', {
    search_term: query,
    ...(resultsCount !== undefined && { results_count: resultsCount })
  });
}

/**
 * Track promo code application
 */
export function trackPromoCode(code: string, success: boolean, discount?: number): void {
  trackEvent('Promo Code', success ? 'Applied' : 'Failed', code, discount);
  
  if (success && discount) {
    ReactGA.event('apply_coupon', {
      coupon: code,
      discount: discount
    });
  }
}

/**
 * Track form submission
 */
export function trackFormSubmission(formName: string, success: boolean): void {
  trackEvent('Form', success ? 'Submit Success' : 'Submit Failed', formName);
  
  if (success) {
    ReactGA.event('generate_lead', {
      form_name: formName
    });
  }
}

/**
 * Track user login
 */
export function trackLogin(method: string): void {
  trackEvent('Auth', 'Login', method);
  
  ReactGA.event('login', {
    method: method
  });
}

/**
 * Track user signup
 */
export function trackSignup(method: string): void {
  trackEvent('Auth', 'Signup', method);
  
  ReactGA.event('sign_up', {
    method: method
  });
}

/**
 * Track user logout
 */
export function trackLogout(): void {
  trackEvent('Auth', 'Logout');
}

/**
 * Track error
 */
export function trackError(errorMessage: string, errorLocation?: string): void {
  trackEvent('Error', errorMessage, errorLocation);
  
  ReactGA.event('exception', {
    description: errorMessage,
    fatal: false
  });
}

/**
 * Track timing (performance)
 */
export function trackTiming(
  category: string,
  variable: string,
  timeMs: number,
  label?: string
): void {
  ReactGA.event('timing_complete', {
    name: variable,
    value: timeMs,
    event_category: category,
    event_label: label
  });
  
  if (isDevelopment) {
    console.log('GA Timing:', { category, variable, timeMs, label });
  }
}

/**
 * Track social share
 */
export function trackShare(method: string, contentType: string, itemId: string): void {
  trackEvent('Share', method, contentType + ' - ' + itemId);
  
  ReactGA.event('share', {
    method: method,
    content_type: contentType,
    item_id: itemId
  });
}

/**
 * Track review submission
 */
export function trackReview(rating: number, itemType: string, itemId: string): void {
  trackEvent('Review', 'Submit', itemType + ' - ' + itemId, rating);
  
  ReactGA.event('review', {
    rating: rating,
    item_type: itemType,
    item_id: itemId
  });
}

/**
 * Set user ID (when logged in)
 */
export function setUserId(userId: string): void {
  try {
    ReactGA.set({ userId });
    
    if (isDevelopment) {
      console.log('GA User ID set:', userId);
    }
  } catch (error) {
    console.error('Failed to set user ID:', error);
  }
}

/**
 * Clear user ID (when logged out)
 */
export function clearUserId(): void {
  try {
    ReactGA.set({ userId: null });
    
    if (isDevelopment) {
      console.log('GA User ID cleared');
    }
  } catch (error) {
    console.error('Failed to clear user ID:', error);
  }
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, any>): void {
  try {
    ReactGA.set(properties);
    
    if (isDevelopment) {
      console.log('GA User Properties:', properties);
    }
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
}

/**
 * Track engagement time
 */
export function trackEngagement(pageName: string, timeSeconds: number): void {
  ReactGA.event('user_engagement', {
    page: pageName,
    engagement_time_msec: timeSeconds * 1000
  });
}

// Export all tracking functions
export default {
  initGA,
  trackPageView,
  trackEvent,
  trackButtonClick,
  trackServiceView,
  trackAddToCart,
  trackRemoveFromCart,
  trackBeginCheckout,
  trackCheckoutStep,
  trackPurchase,
  trackSearch,
  trackPromoCode,
  trackFormSubmission,
  trackLogin,
  trackSignup,
  trackLogout,
  trackError,
  trackTiming,
  trackShare,
  trackReview,
  setUserId,
  clearUserId,
  setUserProperties,
  trackEngagement
};
